import React, {useEffect, useRef} from "react";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {LoadingAnim} from "./CommonHtml";
import {conLog} from "../app/AppContext";

export function useLevelNavigation(routes, defaultPath) {
    const vals = useRef({db: reset(), paramUpdates: {}}).current;
    const location = useLocation();
    const navigate = useNavigate();
    let isInLogin = false;

    useEffect(() => {
        if (!vals.redirTo) return;
        const to = vals.redirTo;
        vals.redirTo = "";
        navigate(to, {replace: true});
        window.scrollTo(0, 0);
    }, [location]);

    if (!routes[location.pathname])
        return histReplace(defaultPath);
    if (vals.prevLoc !== location.pathname + location.search) {
        vals.prevLoc = location.pathname + location.search;
        const parts = location.pathname.split('/');
        let newDb;
        try {
            if (location.search.slice(0,5) === "?code")
                isInLogin = true;
            else
                newDb = location.search && JSON.parse(decodeURIComponent(escape(atob(location.search.slice(1)))));
            newDb?.params?.forEach(p => initParams(p));      // ensure old links
        } catch (err) {
            console.log("Invalid page location:", location.search.slice(1), err.toString());
            return histReplace(defaultPath);
        }

        if (newDb)
            vals.db = newDb;
        else if (parts.length > 2)
            return histReplace(parts.slice(0, 2).join('/'));
        else if (routes[location.pathname])
            vals.db = {depth: 0, params: [], paths: [location.pathname]};
        else
            return histReplace(defaultPath);
        window.scrollTo(0, 0);
    }

    return {
        to, navUp, getCurPath, getPathRoot, getParams, updateParams, getTitle, getDefs, isInLogin,
        getPaths: () => vals.db.paths,
        getParamsRoot: () => vals.db.params[0] || (vals.db.params[0] = {}),
        getDepth: () => vals.db.depth,
        sSubPath: sub => `/${getPathRoot()}/${sub}`,
    };

    function getCurPath() { return vals.db.paths[vals.db.depth] }
    function getPathRoot() { return getCurPath().split('/')[1] }
    function getTitle(idx = vals.db.depth) {
        const path = vals.db.paths[idx];
        const rt = routes[path], {name, tName} = rt;
        const {tObj} = vals.db.params?.[idx] ?? {};
        return !tName || !tObj ? name : tName === "$1" ? tObj[0] : tName.format(...tObj);
    }
    function getDefs(idx = vals.db.depth) {
        const path = vals.db.paths[idx];
        return routes[path].defs || {};
    }

    function to(path, params, {isReplace, isReset} = {}) {
        if (isReset)
            vals.db = reset();
        else if (!isReplace)
            vals.db.depth++;

        vals.db.paths[vals.db.depth] = path;
        vals.db.params[vals.db.depth] = initParams(params);
        // if (localStorage)
        //     localStorage.crumbs = JSON.stringify(vals.db);

        doPush(path);
    }

    function initParams(params = {}) {
        const lp = params._lnParams || (!location.search && location.pathname) || Date.now();
        return Object.assign(params, {_lnParams: lp});
    }

    function _getParams() {
        const oParams = vals.db.params[vals.db.depth] || (vals.db.params[vals.db.depth] = initParams());
        return Object.assign(oParams, vals.paramUpdates[oParams._lnParams] ?? {});
    }
    function getParams() {
        const {_lnParams, ...params} = _getParams();
        return params;
    }

    // provide an object with additional params that will be added to the original page params.
    function updateParams(params) {
        const lp = _getParams()._lnParams;
        if (params && typeof params === "object")
            vals.paramUpdates[lp] = Object.assign(vals.paramUpdates[lp] ?? {}, params);
        return getParams();
    }

    function navUp(toDepth) {
        vals.db.depth = toDepth;
        vals.db.paths = vals.db.paths.slice(0, toDepth+1);
        vals.db.params = vals.db.params.slice(0, toDepth+1);
        doPush(vals.db.paths[toDepth]);
    }

    function reset() { return {depth: 0, params: [], paths: []} }

    function histReplace(newPath) {
        console.log("Redirecting to", newPath);
        vals.redirTo = newPath;
        return {redirected: true};
    }

    function doPush(path) {
        /*
        const s = JSON.stringify(vals.db);
        const URI = encodeURIComponent(s);
        const unesc = unescape(URI);

        if ("CompressionStream" in window) {
            (async () => {
                const stream = new Response(s).body.pipeThrough(new window.CompressionStream('deflate'));
                const compBlob = await new Response(stream).blob();
                //console.log(new Response(stream).arrayBuffer());
                console.log((await compBlob.text()).length);

                const stream2 = new Response(s).body.pipeThrough(new window.CompressionStream('gzip'));
                const compBlob2 = await new Response(stream2).blob();
                //console.log(new Response(stream).arrayBuffer());
                console.log((await compBlob2.text()).length);

                // To be able to pass gzipped data in stacksnippet we host it as a data URI
                // that we do convert to a Blob.
                // The original file is an utf-8 text "Hello world"
                // which is way bigger once compressed, but that's an other story ;)
                // const compressed_blob = await fetch("data:application/octet-stream;base64,H4sIAAAAAAAAE/NIzcnJVyjPL8pJAQBSntaLCwAAAA==")
                //     .then((r) => r.blob());
                //
                // const decompressor = new DecompressionStream("deflate");
                // const decompression_stream = compressed_blob.stream().pipeThrough(decompressor);
                // const decompressed_blob = await new Response(decompression_stream).blob();
                //
                // console.log("decompressed:", await decompressed_blob.text());

            })().catch(console.error);
        }
        else {
            console.error("Your browser doesn't support the Compression API");
        }

        console.log("JSON:", s.length, "URI:", URI.length, "unesc:", unesc.length, "b64:", btoa(unesc).length);
        if (s !== unesc)
            console.log(s, unesc);
        */

        routes[path].instance = (routes[path].instance ?? 0) + 1;

        // unescape URI: leverage UTF-8 processing in URIComponent handling
        navigate({pathname: path, search: btoa(unescape(encodeURIComponent(JSON.stringify(vals.db))))});
    }
}


export const LazyRoutes = ({routes, defaultPath}) => (
    <React.Suspense fallback={<LoadingAnim classNameSize="w-5 h-5"/>}>
        <Routes>
            {Object.entries(routes).map(([path, {component, exact, name, instance}]) => {
                const LazyComp = React.lazy(component);
                return <Route key={path} {...{path, exact, name}} element={<LazyComp key={path + (instance ?? 0)}/>}/>;
            })}
            <Route path="/" exact element={<Navigate replace to={defaultPath}/>}/>
        </Routes>
    </React.Suspense>
);
